import type { AdminSport, TradingNote, TradingNoteSection } from './api'
import { Fragment, useEffect, useState } from 'react'
import { assetUrl } from '../api'
import { fetchSportTradingNotes, TRADING_NOTE_SECTIONS, updateSportTradingNote } from './api'
import AthletesPanel from './AthletesPanel'
import { linkifyText } from './linkify'
import { formatRelative, formatTsUtc } from './time'
import TradingEmailsPanel from './TradingEmailsPanel'
import TradingLinksPanel from './TradingLinksPanel'

interface Props {
  sport: AdminSport
  // Trading+ — same gate as Markets Traded; the whole tab is hidden from
  // lower roles, but the prop keeps the component honest if that changes.
  canEdit: boolean
}

// The per-sport TRADING tab: seven plain-text sections migrated from the
// Trading Bible spreadsheet. Each section has its own edit/save lifecycle
// (one in edit mode at a time) and a freshness line — updated when, by whom —
// which is the main thing the spreadsheet couldn't tell you.
export default function TradingPanel({ sport, canEdit }: Props) {
  const [notes, setNotes] = useState<TradingNote[] | null>(null)
  const [editing, setEditing] = useState<TradingNoteSection | null>(null)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Re-fetch whenever the selected sport changes; drop any in-flight edit.
  useEffect(() => {
    let cancelled = false
    setNotes(null)
    setEditing(null)
    setMsg(null)
    fetchSportTradingNotes(sport.id)
      .then((rows) => {
        if (!cancelled) setNotes(rows)
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
    return () => {
      cancelled = true
    }
  }, [sport.id])

  function startEdit(section: TradingNoteSection) {
    const row = notes?.find(n => n.section === section)
    setDraft(row?.content ?? '')
    setEditing(section)
    setMsg(null)
  }

  async function save(section: TradingNoteSection) {
    setBusy(true)
    setMsg(null)
    try {
      const saved = await updateSportTradingNote(sport.id, section, draft)
      setNotes(prev => (prev ?? []).map(n => (n.section === section
        ? (saved.content === ''
            // Blank save deletes the row server-side — reflect that as an
            // empty section rather than showing a fresh timestamp on nothing.
            ? { section, content: '', updated_at: null, updated_by: null }
            : saved)
        : n)))
      setEditing(null)
      setMsg({ kind: 'ok', text: `Saved ${section.replace(/_/g, ' ')}.` })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <section className="ad-editor ad-editor--full">
      <header className="ad-editor-head">
        <div className="ad-editor-head-stack">
          {sport.logo_url && (
            <div className="ad-editor-logo ad-editor-logo--sport">
              <img src={assetUrl(sport.logo_url)} alt={sport.name} loading="lazy" />
            </div>
          )}
          <h2 className="b-h2">{sport.name}</h2>
        </div>
      </header>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {/* Quick-reference link buttons sit above the long-form notes — they're
          the things traders reach for constantly. */}
      <TradingLinksPanel sportId={sport.id} canEdit={canEdit} />

      {!notes
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : TRADING_NOTE_SECTIONS.map(({ key, label }) => {
            const row = notes.find(n => n.section === key)
            const isEditing = editing === key
            const section = (
              <div key={key} className="ad-editor-section">
                <div className="ad-section-head">
                  <h3 className="b-mono">{label}</h3>
                  {canEdit && (
                    isEditing
                      ? (
                          <div className="ad-section-actions">
                            <button
                              type="button"
                              className="b-btn-ghost b-btn-tiny"
                              onClick={() => setEditing(null)}
                              disabled={busy}
                            >
                              CANCEL
                            </button>
                            <button
                              type="button"
                              className="b-btn b-btn-primary b-btn-tiny"
                              onClick={() => save(key)}
                              disabled={busy}
                            >
                              {busy ? 'SAVING…' : 'SAVE'}
                            </button>
                          </div>
                        )
                      : (
                          <button
                            type="button"
                            className="b-btn b-btn-tiny"
                            onClick={() => startEdit(key)}
                            disabled={editing !== null}
                          >
                            EDIT
                          </button>
                        )
                  )}
                </div>

                {isEditing
                  ? (
                      <textarea
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        className="ad-trading-note-input"
                        rows={Math.max(8, draft.split('\n').length + 1)}
                        spellCheck={false}
                      />
                    )
                  : row?.content
                    ? <div className="ad-trading-note-body">{linkifyText(row.content)}</div>
                    : <p className="b-mono-dim b-tiny">— empty —</p>}

                {!isEditing && row?.updated_at && (
                  <p className="ad-trading-note-meta b-mono-dim b-tiny">
                    updated
                    {' '}
                    <span title={formatTsUtc(row.updated_at)}>{formatRelative(row.updated_at)}</span>
                    {row.updated_by && ` by ${row.updated_by}`}
                  </p>
                )}
              </div>
            )
            if (key !== 'client_delivery') return section
            // EMAIL LIST renders directly below CLIENT DELIVERY — the
            // recipients are who client-delivery mail goes to.
            return (
              <Fragment key={key}>
                {section}
                <TradingEmailsPanel sportId={sport.id} sportName={sport.name} canEdit={canEdit} />
              </Fragment>
            )
          })}

      {/* Age-grouped athlete roster stays below the Trading Bible notes —
          self-contained (own fetch + edit lifecycle). */}
      <AthletesPanel sportId={sport.id} canEdit={canEdit} />
    </section>
  )
}
